import { Component, OnInit } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { FileService } from '../../services/file.service';
import * as _ from 'lodash';

@Component({
  selector: 'egi-file-viewer',
  templateUrl: './file-viewer.component.html',
  styleUrls: ['./file-viewer.component.css'],
})
export class FileViewerComponent implements OnInit {
  @Input() startingFolder: any;
  @Input() showUpload: boolean = true;
  @Input() showButtons: boolean = true;
  @Input() showEdit: boolean = false;
  @Input() showFiles: boolean = true;
  @Input() hiddenFiles: string[] = []; // List of uuids to hide
  @Input() disabledFiles: string[] = []; // List of uuids to disable
  @Input() limitToFolder: any;

  @Output()
  currentPathChange: EventEmitter<any[]> = new EventEmitter<any[]>();

  currentPath: any[];
  public filteredFolders: any[] = [];
  public filteredFiles: any[] = [];
  public filteredSymlinks: any[] = [];
  public folders: any[] = [];
  public files: any[] = [];
  public symlinks: any[] = [];
  public folderModal: boolean = false;
  public folderName: string = '';
  public modalLoading: boolean = false;
  public loading: boolean = false;

  public searchResults = [];
  public searchTerm = '';
  public showResults = false;
  public searchTimeout: any;

  onPathChange(newPath: any[]): void {
    this.currentPath = newPath;
    this.currentPathChange.emit(newPath);
  }

  constructor(private fileService: FileService) {}

  ngOnInit(): void {
    this.currentPath = this.limitToFolder
      ? []
      : [{ name: 'Home', uuid: undefined }];

    if (this.startingFolder?.uuid) {
      this.fileService
        .getFullPathObject(this.startingFolder.uuid)
        .subscribe((res) => {
          if (this.limitToFolder) {
            const index = res.findIndex(
              (item) => item.uuid === this.limitToFolder.uuid,
            );
            res = res.slice(index, res.length);
          }
          this.currentPath.push(...res);

          this.getFiles(this.currentPath[this.currentPath.length - 1]);
        });
    } else {
      this.getFiles();
    }
  }

  openFolder(folder) {
    this.currentPath.push(folder);
    this.getFiles(folder);
  }

  getFiles(folder?: any, clearSearch = true) {
    this.fileService
      .listFiles(folder?.uuid, this.hiddenFiles)
      .subscribe((data) => {
        this.folders = data.filter((item) => item.type === 'folder');
        this.files = this.showFiles
          ? data.filter((item) => item.type === 'file')
          : [];
        this.symlinks = data.filter((item) => item.type === 'symlink');
        this.filteredFiles = this.files.filter((file) =>
          file.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
        );
        this.filteredFolders = this.folders.filter((folder) =>
          folder.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
        );
        this.filteredSymlinks = this.symlinks.filter((symlink) =>
          symlink.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
        );
        this.currentPath = this.currentPath.slice(
          0,
          this.currentPath.map((cp) => cp.uuid).indexOf(folder?.uuid) + 1,
        );
        this.currentPathChange.emit(this.currentPath);
      });
    if (clearSearch) {
      this.searchTerm = '';
      this.onSearch();
    }
  }

  handleDelete() {
    const folder = this.currentPath[this.currentPath.length - 1];
    this.getFiles(folder);
  }

  onFileSelected(event: any) {
    this.loading = true;
    const folder = this.currentPath[this.currentPath.length - 1];
    this.fileService
      .uploadFiles(Array.from(event.target.files), { parent: folder.uuid })
      .subscribe(
        (data) => {
          this.getFiles(folder);
          this.loading = false;
        },
        (error) => {
          alert(error().error.message);
          this.loading = false;
        },
      );
  }

  onFolderCreate() {
    this.modalLoading = true;
    const folder = this.currentPath[this.currentPath.length - 1];
    this.fileService.createFolder(this.folderName, folder.uuid).subscribe(
      (data) => {
        this.modalLoading = false;
        this.handleModalClose();
        this.getFiles(folder);
      },
      (error) => {
        alert(error().error.message);
        this.modalLoading = false;
      },
    );
  }

  handleSync() {
    this.loading = true;
    this.fileService.scanFileSystem().subscribe(() => {
      this.getFiles(this.currentPath[this.currentPath.length - 1]);
      this.loading = false;
    });
    alert(
      'Syncing files, this may take a while depending on the number of files. (Will timeout after 10 minutes, but still running in the background)',
    );
  }

  handleSearch(event: any) {
    const query = event.target.value;
  }

  getCurrentPathName() {
    return this.currentPath.map((item) => item.name).join(' / ');
  }

  handleModalClose() {
    this.folderModal = false;
    this.folderName = '';
  }

  onSearch() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(async () => {
      if (this.searchTerm.trim()) {
        this.fileService.searchFiles(this.searchTerm).subscribe((res) => {
          if (this.limitToFolder) {
            res = res.filter((item) =>
              item.path.split('/').some((p) => p == this.currentPath[0].name),
            );
          }
          this.searchResults = res.map((item) => ({
            ...item,
            path: 'Home/' + item.path,
          }));
        });
      } else {
        this.searchResults = [];
      }
      this.filteredFiles = this.files.filter((file) =>
        file.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
      );
      this.filteredFolders = this.folders.filter((folder) =>
        folder.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
      );
      this.filteredSymlinks = this.symlinks.filter((symlink) =>
        symlink.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
      );
    }, 500); // Debounce delay
  }

  hideResultsWithDelay() {
    setTimeout(() => {
      this.showResults = false;
    }, 150); // Allow click to register
  }

  async handleZip() {
    const folder = this.currentPath[this.currentPath.length - 1];
    this.loading = true;
    this.fileService.downloadFolder(folder.uuid).subscribe(
      (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = folder.name + '.zip'; // Ensure correct filename
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.loading = false;
      },
      (error) => {
        console.error('Error downloading file:', error());
      },
    );
  }

  async selectFile(file: any, filter = false) {
    this.showResults = false;
    if (file.type === 'file') {
      if (filter) {
        this.searchTerm = file.name;
      }
      file = file.parent
        ? await this.fileService.getFile(file.parent).toPromise()
        : undefined;
    } else {
      this.searchTerm = '';
    }
    this.onSearch();
    if (file) {
      this.fileService.getFullPathObject(file.uuid).subscribe((res) => {
        if (this.limitToFolder) {
          const index = res.findIndex(
            (item) => item.uuid === this.limitToFolder.uuid,
          );
          res = res.slice(index, res.length);
        } else {
          res = [{ name: 'Home', uuid: undefined }, ...res];
        }
        this.currentPath = res;
      });
    } else {
      this.currentPath = [{ name: 'Home' }];
    }
    this.getFiles(file, false);
  }
}
